/**
 * API error normalization.
 *
 * Every failure — a structured API error, a network timeout, an HTML error
 * page from a proxy — is turned into the same `ApiError` shape so UI code can
 * branch on `error.code` alone and never has to inspect an Axios response.
 */

import { ERROR_CODE, isPaywallError, paywallContextForError } from '@chordbookpro/shared';

export class ApiError extends Error {
  constructor({ code, message, details = {}, status = 0, requestId = null, cause = null }) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.details = details;
    this.status = status;
    this.requestId = requestId;
    if (cause) this.cause = cause;
  }

  /** True when the client should present a paywall rather than an error. */
  get isPaywall() {
    return isPaywallError(this.code);
  }

  /** Which contextual paywall variant to show, if any. */
  get paywallContext() {
    return paywallContextForError(this.code, this.details);
  }

  /** True when the user's session is no longer valid. */
  get isAuthError() {
    return [
      ERROR_CODE.UNAUTHENTICATED,
      ERROR_CODE.INVALID_TOKEN,
      ERROR_CODE.TOKEN_EXPIRED,
    ].includes(this.code);
  }

  /** True when retrying the same request could plausibly succeed. */
  get isRetryable() {
    return (
      this.code === ERROR_CODE.SERVICE_UNAVAILABLE ||
      this.code === ERROR_CODE.RATE_LIMITED ||
      this.status === 0 ||
      this.status >= 500
    );
  }

  /** True when the server has newer content than the client edited. */
  get isConflict() {
    return this.code === ERROR_CODE.REVISION_CONFLICT;
  }

  /**
   * True when this client build is below the server's minimum supported
   * version. The only recovery is updating from the store — `details` carries
   * `storeUrl`, `minSupported` and `currentVersion` for the update screen.
   */
  get isUpdateRequired() {
    return this.code === ERROR_CODE.APP_UPDATE_REQUIRED;
  }
}

/** Turns any thrown value from Axios into an `ApiError`. */
export function normalizeApiError(error) {
  if (error instanceof ApiError) return error;

  // The request was cancelled deliberately (component unmounted, new search).
  if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError') {
    return new ApiError({
      code: 'REQUEST_CANCELLED',
      message: 'Request cancelled.',
      status: 0,
      cause: error,
    });
  }

  const response = error?.response;

  if (response) {
    const payload = response.data;
    const requestId = response.headers?.['x-request-id'] ?? null;

    // Structured error from our API.
    if (payload && typeof payload === 'object' && payload.error) {
      return new ApiError({
        code: payload.error.code ?? ERROR_CODE.INTERNAL_ERROR,
        message: payload.error.message ?? 'Something went wrong.',
        details: payload.error.details ?? {},
        status: response.status,
        requestId,
        cause: error,
      });
    }

    // Non-JSON error (proxy, gateway, rate limiter upstream).
    return new ApiError({
      code: statusToCode(response.status),
      message: messageForStatus(response.status),
      status: response.status,
      requestId,
      cause: error,
    });
  }

  // No response at all — offline, DNS failure, timeout.
  const isTimeout = error?.code === 'ECONNABORTED';
  return new ApiError({
    code: isTimeout ? ERROR_CODE.SERVICE_UNAVAILABLE : 'NETWORK_ERROR',
    message: isTimeout
      ? 'The request took too long. Check your connection and try again.'
      // Deliberately brand-free: this package is a leaf shared by both clients
      // and has no config of its own, so naming the product here would mean
      // hardcoding it in the one place a rename cannot reach.
      : 'Could not reach the server. Check your connection and try again.',
    status: 0,
    cause: error,
  });
}

function statusToCode(status) {
  switch (status) {
    case 400:
      return ERROR_CODE.BAD_REQUEST;
    case 401:
      return ERROR_CODE.UNAUTHENTICATED;
    case 403:
      return ERROR_CODE.FORBIDDEN;
    case 404:
      return ERROR_CODE.NOT_FOUND;
    case 409:
      return ERROR_CODE.ALREADY_EXISTS;
    case 413:
      return ERROR_CODE.PAYLOAD_TOO_LARGE;
    case 422:
      return ERROR_CODE.VALIDATION_ERROR;
    case 426:
      // Normally the API sends the full envelope and this branch is never
      // reached. It exists so that a proxy which strips the body still yields
      // the update-required code rather than an opaque INTERNAL_ERROR — the
      // difference between the app showing an update screen and showing
      // "something went wrong".
      return ERROR_CODE.APP_UPDATE_REQUIRED;
    case 429:
      return ERROR_CODE.RATE_LIMITED;
    case 503:
      return ERROR_CODE.SERVICE_UNAVAILABLE;
    default:
      return ERROR_CODE.INTERNAL_ERROR;
  }
}

function messageForStatus(status) {
  if (status === 426) return 'This version of the app is out of date. Please update to continue.';
  if (status === 429) return 'Too many requests. Please wait a moment and try again.';
  if (status >= 500) return 'The server is having trouble right now. Please try again shortly.';
  if (status === 404) return 'We could not find what you were looking for.';
  return 'Something went wrong.';
}

/** Extracts field-level validation errors for form display. */
export function getFieldErrors(error) {
  if (!(error instanceof ApiError)) return {};
  if (error.code !== ERROR_CODE.VALIDATION_ERROR) return {};
  return error.details?.fields ?? error.details ?? {};
}
